import * as THREE from 'three';
import { createLogger } from '@/utils/logger';
import type RAPIER from '@dimforge/rapier3d-compat';
import { settings } from '@/config';
import type { Physics } from '@/physics/Physics';
import { BLOOM_LAYER } from '@/postprocessing/SelectiveBloom';
import type { StoneFamily } from '@/audio/StoneAudio';
import type { TickState, Updatable } from '@/types';

const ZERO_VEC = { x: 0, y: 0, z: 0 };

/** A family's audio phase: at rest → lifted out (lift sound played) → returning home (return
 *  sound played, ringing out) → back to idle once fully settled. */
type FamilyPhase = 'idle' | 'lifted' | 'returned';

interface Stone {
  object: THREE.Object3D;
  parent: THREE.Object3D;
  /** World-space geometry centre at rest — forces target this (the GLB bakes position
   *  into vertices, so the node origin is NOT the centre). */
  home: THREE.Vector3;
  /** `home` in the parent's local space (so displacement maps cleanly to local). */
  localHome: THREE.Vector3;
  /** Original local position of the node. */
  restLocal: THREE.Vector3;
  /** Original local rotation of the node. */
  restQuat: THREE.Quaternion;
  /** `restQuat * geometryCentre(objectSpace)` — pivot offset so tumble spins about the
   *  visible centre, not the far baked node origin. */
  pivotG: THREE.Vector3;
  /** Fixed world-space direction this stone bulges along when hovered (up + outward). */
  pushDir: THREE.Vector3;
  /** Half-extents of the stone's world AABB — used to auto-build the contact graph. */
  half: THREE.Vector3;
  /** Cascade rank: 0 = outermost (moves first), 1 = innermost (lags behind). */
  lag: number;
  /** Eased bulge magnitude fed to the spring — the cascade lives here (outer eases in
   *  fast, inner slow), so the outer stones lead and inner ones follow into the gap. */
  gate: number;
  disp: THREE.Vector3; // current world displacement from home
  vel: THREE.Vector3; // displacement velocity (spring)
  pos: THREE.Vector3; // home + disp, reused by the transform/emission step
  /** Rapier body (set when physics is active); else the legacy hand-spring drives it. */
  body?: RAPIER.RigidBody;
  /** Cached body mass so forces can be mass-scaled to acceleration units. */
  mass: number;
}

/** An `N_E` arch mesh paired with its stone `N`. These are DISTINCT geometry (a separate
 *  body/cap of the same voussoir), not co-located duplicates, so at rest they render their
 *  own baked `Baked_1_S_1` texture and simply RIDE their stone `N` through the hover wave
 *  (so the arch stays solid). `material` is only set when the optional additive-glow light-up
 *  is enabled (`settings.stones.emission.enabled`) — off by default; the glow assumed
 *  co-located duplicates and would hide this real geometry. */
interface Twin {
  object: THREE.Object3D;
  /** Additive glow overlay, or null when the twin keeps its baked texture (glow disabled). */
  material: THREE.MeshBasicMaterial | null;
  base: Stone;
  intensity: number;
}

/**
 * igloo.inc-style interactive stones — a hover WAVE that always re-forms the arch.
 *
 *  - Each stone has a fixed outward `pushDir` (up + away from the arch's centre line).
 *  - The cursor's 3D point sets a raw proximity bulge per stone, which is then DIFFUSED
 *    across the auto-built stone contact graph ({@link buildAdjacency}): a hovered stone
 *    pulls its neighbours up with it, they pull theirs, decaying by `wave.coupling` each
 *    ring. So adjacent stones (top, corner, and inner↔outer) rise in sync and clear out
 *    of the way BEFORE a moving stone reaches them — they never interpenetrate.
 *  - A damped spring drives each stone's displacement toward that target; the target is
 *    ZERO whenever the cursor is away, so every stone always glides back to its EXACT
 *    original position. There is no collision solver — the outward fan never drives a
 *    stone into the structure, so nothing is ever trapped off-home.
 */
export class StoneInteraction implements Updatable {
  /**
   * Master switch, as an accessor deliberately: EVERY time interaction is handed back
   * (intro fly-in ends, restart loop, About overlay closes) the stored pointer is forgotten,
   * so the hover wave waits for a REAL mouse move before it can displace anything.
   *
   * Why (2026-07-21, user: "at frame 0 the stones are perfect, at frame 40 they get weird
   * positions"): pointer state accumulates even while disabled — the ENTER click, or any idle
   * mouse drift DURING the fly-in, leaves a parked cursor. The moment interaction re-enabled
   * at frame 40, the very first raycast from that stale cursor landed near the arch (which now
   * fills the screen) and raised the wave under the inner/top stones with zero user intent.
   * Resetting on the rising edge here — rather than at individual call sites — closes every
   * such path at once.
   */
  get enabled(): boolean {
    return this._enabled;
  }
  set enabled(value: boolean) {
    if (value && !this._enabled) this.resetPointer();
    this._enabled = value;
  }
  private _enabled = settings.stones.enabled;

  private stones: Stone[] = [];
  private twins: Twin[] = [];
  /** The additive hover-glow overlays, kept so the glow master switch can toggle them live —
   *  see {@link setGlowEnabled}. */
  private glowMeshes: THREE.Mesh[] = [];
  private lights: THREE.PointLight[] = [];
  /** Live glow tint used by the additive twin overlays (driven by the debug GUI). */
  private readonly glowColor = new THREE.Color(settings.stones.emission.color);
  /** Shared flat material the `N_E` bodies wear when `emission.twinColor` is set (white). */
  private twinBaseMaterial: THREE.MeshBasicMaterial | null = null;
  private readonly _sortBuf: Twin[] = [];

  /**
   * The audio seam (`Experience` wires it to {@link StoneAudio}). Fires per SOUND FAMILY:
   * `'lift'` on the rising edge of that family's stone motion (the stone floats out), `'settle'`
   * on the falling edge (it returns home). `family` is A..E (by stone number); `intensity` is
   * 0..1 = cursor speed when it fired (hard flick → loud, slow graze → soft). Kept a plain
   * callback so this class carries no audio dependency. See {@link updateAudio}.
   */
  onStoneSound?: (family: StoneFamily, kind: 'lift' | 'settle', intensity: number) => void;

  /** Stone indices per sound family (by number), + each family's lift/return state machine. */
  private familyStones: Record<StoneFamily, number[]> = { A: [], B: [], C: [], D: [], E: [] };
  private familyState: Record<StoneFamily, { phase: FamilyPhase; last: number }> = {
    A: { phase: 'idle', last: -1e9 },
    B: { phase: 'idle', last: -1e9 },
    C: { phase: 'idle', last: -1e9 },
    D: { phase: 'idle', last: -1e9 },
    E: { phase: 'idle', last: -1e9 },
  };

  /** Peak-held cursor speed (NDC/sec) — drives the SFX loudness. See {@link trackPointerSpeed}. */
  private pointerSpeed = 0;
  private readonly _lastPointer = new THREE.Vector2();
  private hasLastPointer = false;

  /** Per-stone contact neighbours (indices into `stones`), auto-built from geometry. */
  private adjacency: number[][] = [];
  /** Raw cursor-proximity bulge per stone (before adjacency diffusion). */
  private base = new Float64Array(0);
  /** Diffused bulge magnitude per stone (what actually drives the spring), + scratch. */
  private wave = new Float64Array(0);
  private waveB = new Float64Array(0);

  private physics?: Physics;

  /** Where the cursor currently touches a STONE surface (not the fallback plane) — consumed by
   *  the cursor-follow glow plane every tick. `active` false = cursor is off the stones.
   *  Only stones WITH an emissive `_E` twin activate it (2026-07-20, user: "this only reflects
   *  to those emission mesh of the stones, not the stone" — the plane exists to soften the
   *  emission's edges, so it must light up exactly where the emission can). */
  readonly hover = { active: false, point: new THREE.Vector3(), normal: new THREE.Vector3(), object: null as THREE.Object3D | null };
  /** Base stones that have an emissive twin — the only hover targets for the glow plane. */
  private readonly emissiveBases = new Set<THREE.Object3D>();
  /** Base stone → its emissive twin, for reading the twin's live glow intensity. */
  private readonly twinByBase = new Map<THREE.Object3D, Twin>();
  private readonly log = createLogger('stones');
  private readonly raycaster = new THREE.Raycaster();
  private readonly pointer = new THREE.Vector2();
  private readonly point = new THREE.Vector3();
  private readonly plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  private pointerActive = false;
  private hasPoint = false;

  private readonly _d = new THREE.Vector3();
  private readonly _tmp = new THREE.Vector3();
  private readonly _target = new THREE.Vector3();
  private readonly _accel = new THREE.Vector3();
  private readonly _axis = new THREE.Vector3();
  private readonly _up = new THREE.Vector3(0, 1, 0);
  private readonly _quat = new THREE.Quaternion();
  private readonly _rg = new THREE.Vector3();
  private readonly _box = new THREE.Box3();

  constructor(
    private readonly camera: THREE.Camera,
    private readonly dom: HTMLElement,
  ) {
    this.dom.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.dom.addEventListener('pointerleave', this.onPointerLeave, { passive: true });
  }

  setStones(objects: THREE.Object3D[]): void {
    const scale = new THREE.Vector3();
    const geoCenter = new THREE.Vector3();
    this.stones = objects
      .filter((object) => object.parent)
      .map((object) => {
        const mesh = object as THREE.Mesh;
        geoCenter.set(0, 0, 0);
        if (mesh.geometry) {
          if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
          mesh.geometry.boundingBox?.getCenter(geoCenter);
        }
        object.getWorldScale(scale);

        const parent = object.parent as THREE.Object3D;
        const restQuat = object.quaternion.clone();
        this._box.setFromObject(object);
        const home = this._box.getCenter(new THREE.Vector3());
        const half = this._box.getSize(new THREE.Vector3()).multiplyScalar(0.5);
        return {
          object,
          parent,
          home,
          localHome: parent.worldToLocal(home.clone()),
          restLocal: object.position.clone(),
          restQuat,
          pivotG: geoCenter.clone().applyQuaternion(restQuat),
          pushDir: new THREE.Vector3(0, 1, 0), // set below once the centre is known
          half,
          lag: 0, // set by computeLayers() (outer → inner cascade rank)
          gate: 0,
          disp: new THREE.Vector3(),
          vel: new THREE.Vector3(),
          pos: home.clone(),
          mass: 0,
        };
      });

    this.computePushDirs();
    this.buildAdjacency();
    this.computeLayers();
    this.classifyFamilies();
  }

  /** Re-run the family split (debug GUI, after a family's stone list / keystone changes). */
  refreshGroups(): void {
    this.classifyFamilies();
  }

  /** Map each stone to its SOUND FAMILY by number, from `settings.audio.stoneFx.families`. The
   *  keystone list (E) OVERRIDES a stone's A-D membership. Stones whose number isn't listed get
   *  no family (silent) — with the user's lists that's none, all 35 are covered by A-D. */
  private classifyFamilies(): void {
    const fam = settings.audio.stoneFx.families;
    const numToFamily = new Map<number, StoneFamily>();
    for (const key of ['A', 'B', 'C', 'D'] as StoneFamily[]) {
      for (const n of fam[key].stones) numToFamily.set(n, key);
    }
    for (const n of fam.E.stones) numToFamily.set(n, 'E'); // keystone overrides its A-D family

    this.familyStones = { A: [], B: [], C: [], D: [], E: [] };
    for (let i = 0; i < this.stones.length; i += 1) {
      const num = Number.parseInt(this.stones[i]!.object.name, 10);
      const f = Number.isNaN(num) ? undefined : numToFamily.get(num);
      if (f) this.familyStones[f].push(i);
    }
    for (const key of ['A', 'B', 'C', 'D', 'E'] as StoneFamily[]) {
      this.familyState[key] = { phase: 'idle', last: -1e9 };
    }
  }

  /**
   * Promote every stone to a gravity-free Rapier dynamic body (convex hull) and add
   * the static structure (`colliders`) as fixed trimeshes. After this the hover wave
   * is solved by the physics engine, so stones collide with the pillars and each
   * other on the way out, then are sprung back home.
   */
  setPhysics(physics: Physics, colliders: THREE.Object3D[], floorY: number): void {
    if (!settings.stones.physics.enabled) return;
    this.physics = physics;
    const cfg = settings.stones.physics;

    for (const stone of this.stones) {
      const mesh = stone.object as THREE.Mesh;
      if (!mesh.isMesh || !mesh.geometry) continue;
      const body = physics.addDynamicBody(mesh, stone.home, {
        density: cfg.density,
        linearDamping: cfg.linearDamping,
        angularDamping: cfg.angularDamping,
        shrink: cfg.colliderShrink,
      });
      if (!body) {
        this.log.warn(`stone "${mesh.name}" has NO physics body — driving it with the visual spring instead.`);
        continue;
      }
      stone.body = body;
      stone.mass = body.mass() || 1;
    }

    for (const object of colliders) {
      const mesh = object as THREE.Mesh;
      if (mesh.isMesh && mesh.geometry) physics.addStaticTrimesh(mesh);
    }
    physics.addFloor(floorY);
  }

  /** Outward fan: each stone bulges up + away from the arch's central vertical axis,
   *  staying in its own depth slice (no Z), so the wave never drives into the structure. */
  private computePushDirs(): void {
    if (!this.stones.length) return;
    let cx = 0;
    let minY = Infinity;
    for (const stone of this.stones) {
      cx += stone.home.x;
      minY = Math.min(minY, stone.home.y);
    }
    cx /= this.stones.length;
    const baseY = minY - 0.5; // axis sits just below the lowest stone → everything lifts

    for (const stone of this.stones) {
      stone.pushDir.set(stone.home.x - cx, stone.home.y - baseY, 0);
      if (stone.pushDir.lengthSq() < 1e-6) stone.pushDir.set(0, 1, 0);
      else stone.pushDir.normalize();
    }
  }

  /**
   * Auto-builds the stone contact graph: two stones are neighbours when their world
   * AABBs overlap after a small inflation (to bridge mortar gaps). This captures BOTH
   * angular neighbours (along the arch ring) AND radial neighbours (inner↔outer), so the
   * diffused wave keeps every touching pair moving in sync — no manual pair list needed.
   */
  private buildAdjacency(): void {
    const n = this.stones.length;
    this.adjacency = Array.from({ length: n }, () => [] as number[]);
    this.base = new Float64Array(n);
    this.wave = new Float64Array(n);
    this.waveB = new Float64Array(n);

    const inflate = 1 + settings.stones.wave.adjacencyInflate;
    for (let i = 0; i < n; i += 1) {
      const a = this.stones[i]!;
      for (let j = i + 1; j < n; j += 1) {
        const b = this.stones[j]!;
        const sx = (a.half.x + b.half.x) * inflate;
        const sy = (a.half.y + b.half.y) * inflate;
        const sz = (a.half.z + b.half.z) * inflate;
        if (
          Math.abs(a.home.x - b.home.x) < sx &&
          Math.abs(a.home.y - b.home.y) < sy &&
          Math.abs(a.home.z - b.home.z) < sz
        ) {
          this.adjacency[i]!.push(j);
          this.adjacency[j]!.push(i);
        }
      }
    }
  }

  /**
   * Ranks each stone OUTER→INNER by its radius in the arch plane (drops the thin depth
   * axis), normalized 0..1. `lag` = 1 at the innermost ring, 0 at the outermost, so the
   * per-frame ease can run outer stones fast (lead) and inner stones slow (follow).
   */
  private computeLayers(): void {
    const n = this.stones.length;
    if (!n) return;

    const center = new THREE.Vector3();
    for (const s of this.stones) center.add(s.home);
    center.multiplyScalar(1 / n);

    const min = new THREE.Vector3(Infinity, Infinity, Infinity);
    const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity);
    for (const s of this.stones) {
      min.min(s.home);
      max.max(s.home);
    }
    const span = new THREE.Vector3().subVectors(max, min);
    const depth = span.x <= span.y && span.x <= span.z ? 'x' : span.y <= span.z ? 'y' : 'z';
    const axA: 'x' | 'y' | 'z' = depth === 'x' ? 'y' : 'x';
    const axB: 'x' | 'y' | 'z' = depth === 'z' ? 'y' : 'z';

    let rmin = Infinity;
    let rmax = -Infinity;
    const radii = this.stones.map((s) => {
      const r = Math.hypot(s.home[axA] - center[axA], s.home[axB] - center[axB]);
      rmin = Math.min(rmin, r);
      rmax = Math.max(rmax, r);
      return r;
    });
    const range = Math.max(rmax - rmin, 1e-4);
    for (let i = 0; i < n; i += 1) {
      this.stones[i]!.lag = 1 - (radii[i]! - rmin) / range; // outer→0 (fast), inner→1 (slow)
    }
  }

  /**
   * Per-frame wave: seed each stone with its raw cursor-proximity bulge, then DIFFUSE
   * across the contact graph so a hovered stone pulls its neighbours up with it (and they
   * pull theirs, decaying by `coupling` each ring). Result: adjacent stones rise together
   * and clear the way before a moving stone can reach them.
   */
  private computeWave(cfg: typeof settings.stones): void {
    const n = this.stones.length;
    for (let i = 0; i < n; i += 1) this.base[i] = this.magAt(this.stones[i]!.home, cfg);

    let cur = this.wave;
    let nxt = this.waveB;
    cur.set(this.base);

    const coupling = cfg.wave.coupling;
    for (let p = 0; p < cfg.wave.passes; p += 1) {
      for (let i = 0; i < n; i += 1) {
        let m = this.base[i]!;
        const nbrs = this.adjacency[i]!;
        for (let k = 0; k < nbrs.length; k += 1) {
          const v = coupling * cur[nbrs[k]!]!;
          if (v > m) m = v;
        }
        nxt[i] = m;
      }
      const t = cur;
      cur = nxt;
      nxt = t;
    }
    this.wave = cur;
    this.waveB = nxt;
  }

  /**
   * Forget the current pointer until the NEXT real pointermove (2026-07-21, user: "at frame 0
   * it's perfect, but when it animates to frame 40 the inner/top stones' positions change").
   *
   * The ENTER click leaves a pointer position parked at screen centre; the intro then flies the
   * camera right up to the arch, and the moment interaction re-enables, the raycast from that
   * STALE, never-moved cursor lands on the upper-inner stones — displacing them with zero user
   * intent. Calling this when interaction is handed back makes the wave wait for an actual
   * mouse move instead.
   */
  resetPointer(): void {
    this.pointerActive = false;
    this.hasLastPointer = false;
  }

  private onPointerMove = (event: PointerEvent): void => {
    const rect = this.dom.getBoundingClientRect();
    this.pointer.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    this.pointer.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    this.pointerActive = true;
  };

  private onPointerLeave = (): void => {
    this.pointerActive = false;
  };

  /** The hovered stone's emissive-twin intensity (0..1) RIGHT NOW — 0 when nothing emits.
   *  Drives the cursor glow's visibility so it only ever brightens lit emission, never the
   *  resting baked stone (2026-07-20, user: "it does not affect our stone back texture — it is
   *  just to make a smooth glow of the emission texture"). */
  hoverEmissionIntensity(): number {
    const object = this.hover.object;
    if (!object) return 0;
    return this.twinByBase.get(object)?.intensity ?? 0;
  }

  private resolvePoint(): void {
    this.hasPoint = false;
    this.hover.active = false;
    this.hover.object = null;
    if (!this.pointerActive || !this.stones.length) return;
    this.raycaster.setFromCamera(this.pointer, this.camera);

    const hits = this.raycaster.intersectObjects(
      this.stones.map((stone) => stone.object),
      false,
    );
    if (hits.length) {
      const hit = hits[0]!;
      this.point.copy(hit.point);
      this.hasPoint = true;
      // Publish the surface hit for the cursor-follow glow plane ({@link CursorGlow}): world
      // position + world normal of the stone face under the cursor. `transformDirection` uses
      // the rotation part of matrixWorld only, which is exactly right for a normal here (the
      // stones carry no shear/scale — their vertices are world-baked, transforms are offsets).
      this.hover.active = this.emissiveBases.has(hit.object);
      this.hover.object = this.hover.active ? hit.object : null;
      this.hover.point.copy(hit.point);
      if (hit.face) {
        this.hover.normal.copy(hit.face.normal).transformDirection(hit.object.matrixWorld);
      } else {
        this.hover.normal.set(0, 0, 1);
      }
      return;
    }
    if (this.raycaster.ray.intersectPlane(this.plane, this._tmp)) {
      this.point.copy(this._tmp);
      this.hasPoint = true;
    }
  }

  update(tick: TickState): void {
    if (!this.enabled || !this.stones.length) return;
    const dt = Math.min(tick.delta, 1 / 30);
    const cfg = settings.stones;
    this.resolvePoint();
    this.trackPointerSpeed(dt);
    this.computeWave(cfg);

    if (this.physics) this.updatePhysics(tick.delta, cfg);
    else this.updateSpring(dt, cfg);

    if (this.twins.length) this.updateEmission(dt, cfg.maxOffset);
    if (this.onStoneSound) this.updateAudio(tick.elapsed, cfg.maxOffset);
  }

  /**
   * Peak-held cursor speed in NDC/sec, driving how LOUD the SFX hits. Peak-hold-with-decay
   * (not instantaneous): the stone's spring displacement lags the cursor by a few frames, so
   * by the time the sound fires the flick that caused it may already be slowing — holding the
   * recent peak means a hard flick is still remembered at the trigger moment. Decays toward 0
   * over ~1/`speedDecay` seconds when the cursor slows or stops.
   */
  private trackPointerSpeed(dt: number): void {
    const decay = Math.exp(-settings.audio.stoneFx.speedDecay * dt);
    let instant = 0;
    if (this.pointerActive && this.hasLastPointer) {
      instant = this._lastPointer.distanceTo(this.pointer) / Math.max(dt, 1e-4);
    }
    // Hold the peak, let it bleed off — max of the decayed previous value and this frame's speed.
    this.pointerSpeed = Math.max(instant, this.pointerSpeed * decay);
    if (this.pointerActive) {
      this._lastPointer.copy(this.pointer);
      this.hasLastPointer = true;
    } else {
      this.hasLastPointer = false; // cursor left — don't count the re-entry jump as motion
    }
  }

  /** Cursor speed → 0..1 loudness intensity (below `minSpeed` → 0, above `maxSpeed` → 1). */
  private speedIntensity(): number {
    const cfg = settings.audio.stoneFx;
    const span = Math.max(cfg.maxSpeed - cfg.minSpeed, 1e-4);
    return THREE.MathUtils.clamp((this.pointerSpeed - cfg.minSpeed) / span, 0, 1);
  }

  /**
   * Per SOUND FAMILY, fire `'lift'` on the rising edge of that family's peak stone MOTION and
   * `'settle'` on the falling edge. Motion is the actual spring displacement ÷ `maxOffset`, so
   * sound is locked to visible movement — hover a family's stones and they float out (lift), let
   * them return and they set down (settle). Working per family (not per stone) means one clean
   * sound as you engage a region, not 35 at once; hysteresis + cooldown keep it from chattering.
   * The LIFT loudness comes from cursor speed at that moment; settle is a fixed gentle level (in
   * {@link StoneAudio}). Runs each frame; both the physics and spring paths keep `stone.disp` live.
   */
  private updateAudio(elapsed: number, maxOffset: number): void {
    const cfg = settings.audio.stoneFx;
    if (!cfg.enabled || !this.onStoneSound) return;
    const inv = 1 / Math.max(maxOffset, 1e-4);
    const intensity = this.speedIntensity();
    const families: StoneFamily[] = ['A', 'B', 'C', 'D', 'E'];

    for (const family of families) {
      const idx = this.familyStones[family];
      if (!idx.length) continue;

      let peak = 0;
      for (let k = 0; k < idx.length; k += 1) {
        const d = this.stones[idx[k]!]!.disp.length() * inv;
        if (d > peak) peak = d;
      }

      // idle → LIFT (rising) → lifted → RETURN (still visibly descending) → returned → idle.
      const st = this.familyState[family];
      if (st.phase === 'idle') {
        if (peak >= cfg.triggerLevel && elapsed - st.last >= cfg.cooldown) {
          this.onStoneSound(family, 'lift', intensity);
          st.phase = 'lifted';
          st.last = elapsed;
        }
      } else if (st.phase === 'lifted') {
        if (peak <= cfg.returnLevel) {
          this.onStoneSound(family, 'settle', intensity); // return fires while still moving back
          st.phase = 'returned';
        }
      } else if (peak <= cfg.resetLevel) {
        st.phase = 'idle'; // fully home — re-armed (no sound; the return's fade tail rings on)
      }
    }
  }

  /** Cursor-proximity bulge magnitude for a point, measured against its REST centre. */
  private magAt(centre: THREE.Vector3, cfg: typeof settings.stones): number {
    if (!this.hasPoint) return 0;
    const dist = centre.distanceTo(this.point);
    if (dist >= cfg.radius) return 0;
    return cfg.maxPush * Math.pow(1 - dist / cfg.radius, cfg.falloffPower);
  }

  /**
   * Rapier path — CLEAN CORE (2026-07-21 rewrite; user: "something weird happening with the
   * original positions of all stones — rewrite it neatly").
   *
   * ONE job: produce each stone's world DISPLACEMENT from `home` (spring + collisions), then
   * hand rendering to the SAME {@link applyTransforms} the spring path uses. The old code kept
   * a second, parallel pose system for physics (rebuild `liveWorld = T·R·(-home)·restWorld`
   * per frame, then matrix-decompose) — its reconstruction error is what showed up as every
   * stone sitting subtly off its authored pose. Now rest pose is `restLocal`/`restQuat`
   * VERBATIM: a settled arch is byte-identical to the export, by construction.
   *
   * Body lifecycle per stone, explicit and total:
   *  - RETURNING/IDLE (wave 0, gate settled): body is KINEMATIC and GLIDES to `home`, snapping
   *    exact when sub-mm. Contacts have zero authority over kinematic bodies — nothing can
   *    hold or push a resting stone — while it still acts as a solid wall for active
   *    neighbours. This is what guarantees "always returns to the original position".
   *  - ACTIVE (wave or gate alive): body is DYNAMIC — spring toward `home + pushDir·gate`,
   *    colliding with neighbours and the structure.
   *  - NO BODY (hull build failed): pure visual wave via the same displacement model.
   */
  private updatePhysics(delta: number, cfg: typeof settings.stones): void {
    const ph = cfg.physics;
    const wv = cfg.wave;
    const clampDist = cfg.maxOffset * 2;

    // 1. DRIVE — ease gates, pick each body's mode, spring the active ones.
    for (let i = 0; i < this.stones.length; i += 1) {
      const stone = this.stones[i]!;
      const rate = wv.leadRate * (1 - stone.lag) + wv.lagRate * stone.lag;
      stone.gate += (this.wave[i]! - stone.gate) * (1 - Math.exp(-rate * delta));

      const body = stone.body;
      if (!body) {
        stone.disp.copy(stone.pushDir).multiplyScalar(stone.gate);
        continue;
      }

      // RESTORED to the committed design (2026-07-21, user: "check the previous commit — that
      // is what I want"): stones NEVER collide with each other (see Physics collision groups) —
      // the wave choreography itself keeps neighbours apart — so the spring alone always
      // converges to EXACTLY home and no body-type juggling or pinning is needed. The
      // stone-vs-stone experiment (hulls, boot resolver, kinematic idle pins) is what made
      // rest positions drift, and it is gone wholesale.
      // Spring toward the wave target (Rapier forces persist — reset, then re-apply).
      body.resetForces(false);
      this._target.copy(stone.home).addScaledVector(stone.pushDir, stone.gate);
      const t = body.translation();
      const v = body.linvel();
      this._accel.set(
        (this._target.x - t.x) * ph.springStiffness - v.x * ph.springDamping,
        (this._target.y - t.y) * ph.springStiffness - v.y * ph.springDamping,
        (this._target.z - t.z) * ph.springStiffness - v.z * ph.springDamping,
      );
      body.addForce(
        { x: this._accel.x * stone.mass, y: this._accel.y * stone.mass, z: this._accel.z * stone.mass },
        true,
      );
    }

    this.physics!.step(delta);

    // 2. READ BACK — displacement only, with the runaway guards.
    for (const stone of this.stones) {
      const body = stone.body;
      if (!body) continue; // bodyless disp was set in the drive loop
      const t = body.translation();
      stone.disp.set(t.x - stone.home.x, t.y - stone.home.y, t.z - stone.home.z);
      if (!Number.isFinite(stone.disp.lengthSq())) {
        body.setTranslation(stone.home, false);
        body.setLinvel(ZERO_VEC, false);
        stone.disp.set(0, 0, 0);
      } else if (stone.disp.lengthSq() > clampDist * clampDist) {
        stone.disp.setLength(clampDist); // clamp to range, keep it continuous (no teleports)
        body.setTranslation(
          { x: stone.home.x + stone.disp.x, y: stone.home.y + stone.disp.y, z: stone.home.z + stone.disp.z },
          true,
        );
        body.setLinvel(ZERO_VEC, false);
      }
    }

    // 3. RENDER — every stone through the ONE shared pose path.
    for (const stone of this.stones) stone.pos.copy(stone.home).add(stone.disp);
    this.applyTransforms(cfg.rotation, cfg.maxOffset);
  }

  /** Legacy hand-rolled spring (used when Rapier is unavailable). */
  private updateSpring(dt: number, cfg: typeof settings.stones): void {
    const epsSq = cfg.settleEps * cfg.settleEps;
    const wv = cfg.wave;
    for (let i = 0; i < this.stones.length; i += 1) {
      const stone = this.stones[i]!;
      // Layer-eased magnitude → outer stones lead, inner stones follow into the gap.
      const target = this.wave[i]!;
      const rate = wv.leadRate * (1 - stone.lag) + wv.lagRate * stone.lag;
      stone.gate += (target - stone.gate) * (1 - Math.exp(-rate * dt));
      const mag = stone.gate;
      this._target.copy(stone.pushDir).multiplyScalar(mag);

      // Damped spring toward the target displacement.
      this._accel.copy(this._target).sub(stone.disp).multiplyScalar(cfg.stiffness);
      this._accel.addScaledVector(stone.vel, -cfg.damping);
      stone.vel.addScaledVector(this._accel, dt);
      stone.disp.addScaledVector(stone.vel, dt);

      const offset = stone.disp.length();
      if (offset > cfg.maxOffset) {
        stone.disp.multiplyScalar(cfg.maxOffset / offset);
        stone.vel.multiplyScalar(0.5);
      }

      // Idle snap → exact original position so the arch always re-forms perfectly.
      if (
        target === 0 &&
        stone.gate < cfg.settleEps &&
        stone.disp.lengthSq() < epsSq &&
        stone.vel.lengthSq() < epsSq
      ) {
        stone.disp.set(0, 0, 0);
        stone.vel.set(0, 0, 0);
        stone.gate = 0;
      }

      stone.pos.copy(stone.home).add(stone.disp);
    }

    this.applyTransforms(cfg.rotation, cfg.maxOffset);
  }

  private applyTransforms(rotation: number, maxOffset: number): void {
    for (const stone of this.stones) {
      const offset = stone.disp.length();

      // Tumble about the visible centre (not the baked node origin).
      if (offset > 1e-4) {
        this._axis.copy(this._up).cross(stone.disp);
        if (this._axis.lengthSq() > 1e-6) {
          this._axis.normalize();
          this._quat.setFromAxisAngle(this._axis, Math.min(offset, maxOffset) * rotation);
        } else {
          this._quat.identity();
        }
      } else {
        this._quat.identity();
      }
      stone.object.quaternion.copy(this._quat).multiply(stone.restQuat);

      // Translation: target centre → parent-local, take delta from rest centre, add to
      // the node's original local position; compensate the rotation pivot (g − R·g).
      this._tmp.copy(stone.pos);
      stone.parent.worldToLocal(this._tmp);
      this._d.copy(this._tmp).sub(stone.localHome);
      this._rg.copy(stone.pivotG).applyQuaternion(this._quat);
      stone.object.position.copy(stone.restLocal).add(this._d).add(stone.pivotG).sub(this._rg);
    }
  }

  /**
   * Registers the emissive twins (`N_E`). Each twin overlays its stone `N` with a
   * dedicated emissive material (cloned from the stone's, so at rest it matches the
   * default texture); its emissive intensity is driven by the stone's displacement.
   */
  setEmission(objects: THREE.Object3D[], scene: THREE.Scene): void {
    this.twins = [];
    if (!objects.length) return;

    const byName = new Map(this.stones.map((stone) => [stone.object.name, stone]));
    const glow = settings.stones.emission.enabled;
    this.glowColor.set(settings.stones.emission.color);

    // User wants the `_E` stone bodies rendered PLAIN BRIGHT WHITE (2026-07-13) rather than
    // their baked texture — one shared unlit material for all of them (null keeps the texture).
    const twinColor = settings.stones.emission.twinColor;
    const twinMaterial = twinColor
      ? new THREE.MeshBasicMaterial({ color: new THREE.Color(twinColor), toneMapped: false, side: THREE.DoubleSide })
      : null;
    this.twinBaseMaterial = twinMaterial;

    for (const object of objects) {
      const mesh = object as THREE.Mesh;
      if (!mesh.isMesh) continue;
      const base = byName.get(object.name.replace(/_E$/i, ''));
      if (!base) continue;

      if (twinMaterial) mesh.material = twinMaterial; // plain white `_E` bodies

      // The twin is REAL arch geometry, so KEEP its baked `Baked_1_S_1` material (it must stay
      // textured). For the hover light-up we add a SEPARATE additive glow OVERLAY as a CHILD of
      // the twin (same geometry, so it hugs the stone and inherits its transform/ride for free)
      // rather than swapping the twin's own material — that earlier swap is exactly what hid the
      // baked texture. The overlay is transparent at rest (opacity 0) and fades in with the
      // stone's displacement; `toneMapped:false` + additive lets it run HDR-bright and bloom, and
      // `depthWrite:false` + polygon offset keeps it from z-fighting the baked stone underneath.
      let material: THREE.MeshBasicMaterial | null = null;
      if (glow) {
        material = new THREE.MeshBasicMaterial({
          color: this.glowColor.clone(),
          transparent: true,
          opacity: 0,
          blending: THREE.AdditiveBlending,
          depthWrite: false,
          toneMapped: false,
          polygonOffset: true,
          polygonOffsetFactor: -1,
          polygonOffsetUnits: -1,
          // Stamp this twin's SILHOUETTE into the stencil buffer (even at opacity 0 — the draw
          // still runs, it just adds no colour). The cursor glow tests against this stamp, so
          // its light lands ONLY on emission-mesh pixels, never the baked stone around them
          // (2026-07-20, user: "it should only affect the emission stone").
          stencilWrite: true,
          stencilRef: 1,
          stencilFunc: THREE.AlwaysStencilFunc,
          stencilZPass: THREE.ReplaceStencilOp,
        });
        const glowMesh = new THREE.Mesh(mesh.geometry, material);
        glowMesh.name = `${mesh.name}_glow`;
        glowMesh.castShadow = false;
        glowMesh.receiveShadow = false;
        glowMesh.renderOrder = 2;
        // Opt the hover glow into the bloom layer — one of the only two things allowed to glow.
        // Both the layer and the mesh's visibility follow the glow master switch (see
        // `setGlowEnabled`); the overlay is additive, so it would glow on its own if left visible.
        if (settings.selectiveBloom.enabled) glowMesh.layers.enable(BLOOM_LAYER);
        else glowMesh.visible = false;
        this.glowMeshes.push(glowMesh);
        mesh.add(glowMesh); // child: rides the twin (which rides its stone) automatically
      }
      mesh.visible = true;

      this.twins.push({ object, material, base, intensity: 0 });
      this.emissiveBases.add(base.object); // this stone can emit → it can carry the glow plane
      this.twinByBase.set(base.object, this.twins[this.twins.length - 1]!);
    }

    const lc = settings.stones.emission.light;
    if (glow && lc.enabled && this.twins.length) {
      const lightColor = new THREE.Color(lc.color);
      for (let i = 0; i < lc.count; i += 1) {
        const light = new THREE.PointLight(lightColor, 0, lc.distance, lc.decay);
        light.visible = false;
        if (i < lc.shadowCount) {
          light.castShadow = true;
          light.shadow.mapSize.set(lc.shadowMapSize, lc.shadowMapSize);
          light.shadow.bias = -0.005;
          light.shadow.camera.near = 0.2;
          light.shadow.camera.far = lc.distance;
        }
        scene.add(light);
        this.lights.push(light);
      }
    }
  }

  /** Live-update the surface glow colour on every emissive twin (debug GUI). */
  setEmissionColor(color: THREE.ColorRepresentation): void {
    this.glowColor.set(color);
  }

  /** Live-update the colour of the spill point-lights (debug GUI). */
  setLightColor(color: THREE.ColorRepresentation): void {
    for (const light of this.lights) light.color.set(color);
  }

  private updateEmission(dt: number, maxOffset: number): void {
    const cfg = settings.stones.emission;
    const span = Math.max(maxOffset - cfg.threshold, 1e-4);
    for (const twin of this.twins) {
      // Ride the base stone so the twin (the same voussoir's other half) stays locked to it
      // through the hover wave and the arch never splits apart.
      twin.object.position.copy(twin.base.object.position);
      twin.object.quaternion.copy(twin.base.object.quaternion);
      // No glow material ⇒ the twin just wears its baked texture; following is all it needs.
      if (!twin.material) continue;

      const offset = twin.base.disp.length();
      const target = THREE.MathUtils.clamp((offset - cfg.threshold) / span, 0, 1);
      const rate = target > twin.intensity ? cfg.attack : cfg.release;
      twin.intensity += (target - twin.intensity) * (1 - Math.exp(-rate * dt));
      // HDR glow tint (colour × intensity, can exceed 1 for bloom) modulated in by the
      // per-twin opacity — 0 at rest (invisible) up to full on hover.
      twin.material.color.copy(this.glowColor).multiplyScalar(cfg.intensity);
      twin.material.opacity = twin.intensity;
    }
    if (this.lights.length) this.updateLights(dt);
  }

  /** Assign each pooled light to one of the currently brightest glowing stones. */
  private updateLights(dt: number): void {
    const lc = settings.stones.emission.light;
    this._sortBuf.length = 0;
    for (const twin of this.twins) this._sortBuf.push(twin);
    this._sortBuf.sort((a, b) => b.intensity - a.intensity);

    const k = 1 - Math.exp(-12 * dt);
    for (let i = 0; i < this.lights.length; i += 1) {
      const light = this.lights[i]!;
      const twin = this._sortBuf[i];
      const target = twin ? twin.intensity * lc.intensity : 0;
      if (twin && twin.intensity > 0.02) light.position.copy(twin.base.pos);
      light.intensity += (target - light.intensity) * k;
      light.visible = light.intensity > 0.01;
    }
  }

  /**
   * Turn the stones' hover glow on/off LIVE, following the glow master switch
   * (`settings.selectiveBloom.enabled`, driven by the "postprocessing" GUI toggle).
   *
   * Visibility matters as much as the layer here: the overlay uses AdditiveBlending, so a visible
   * overlay glows by itself even with the bloom pass switched off entirely.
   */
  setGlowEnabled(on: boolean): void {
    for (const glowMesh of this.glowMeshes) {
      if (on) glowMesh.layers.enable(BLOOM_LAYER);
      else glowMesh.layers.disable(BLOOM_LAYER);
      glowMesh.visible = on;
    }
  }

  dispose(): void {
    this.dom.removeEventListener('pointermove', this.onPointerMove);
    this.dom.removeEventListener('pointerleave', this.onPointerLeave);
    for (const light of this.lights) light.parent?.remove(light);
    this.lights = [];
    this.twinBaseMaterial?.dispose();
    this.twinBaseMaterial = null;
    this.stones = [];
    this.twins = [];
  }
}
